import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';

import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GlobalSearchService, SearchResult } from './global-search.service';
import { HighlightPipe } from './highlight.pipe';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [HighlightPipe],
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  searchText = signal<string>('');
  results = signal<SearchResult[]>([]);
  term = signal<string>('');
  open = signal<boolean>(false);

  // Only typing feeds this; programmatic writes to `searchText` must not
  // re-trigger the search (this is what `{emitEvent: false}` used to do).
  private typed$ = new Subject<string>();
  private sub?: Subscription;

  constructor(
    private searchService: GlobalSearchService,
    private router: Router,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    // Show results 1s after the last keystroke.
    this.sub = this.typed$
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => {
        this.term.set((value || '').trim());
        this.results.set(this.searchService.search(this.term()));
        this.open.set(!!this.term());
      });
  }

  onSearchInput(value: string): void {
    this.searchText.set(value);
    this.typed$.next(value);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  select(entry: SearchResult): void {
    this.router.navigateByUrl(entry.path);
    this.searchText.set('');
    this.term.set('');
    this.results.set([]);
    this.open.set(false);
  }

  onFocus(): void {
    if (this.term() && this.results().length) {
      this.open.set(true);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open.set(false);
  }
}
